import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import toast from "react-hot-toast";
import { api } from "../services/api";
import { useAuth } from "./AuthContext";

export interface Headline {
  id: string;
  dailyMessage: string;
  dailyMessage_in_thai: string;
  dailyMessage_in_arabic: string;
  dailyQuestion: string;
  dailyQuestion_in_thai: string;
  dailyQuestion_in_arabic: string;
  lucky_color: string;
  energy_level: string;
  golden_hour: string;
  color_code: string;
  date: Date;
  createdAt: Date;
  updatedAt: Date;
}

interface HeadlineContextType {
  headlines: Headline[];
  isLoading: boolean;

  // Similar naming as your SubCategoryContext
  fetchAllHeadlines: () => Promise<void>;
  getAllHeadlines: () => Promise<{ data: Headline[]; meta: any }>;

  // "getById" equivalent (your backend uses date as identifier)
  getHeadlineByDate: (date: string | Date) => Promise<Headline | null>;

  // Local helpers
  getHeadlineFromStoreByDate: (date: string | Date) => Headline | undefined;
}

const HeadlineContext = createContext<HeadlineContextType | undefined>(
  undefined,
);

export const useHeadlines = () => {
  const context = useContext(HeadlineContext);
  if (!context) {
    throw new Error("useHeadlines must be used within a HeadlineProvider");
  }
  return context;
};

interface HeadlineProviderProps {
  children: ReactNode;
}

export const HeadlineProvider: React.FC<HeadlineProviderProps> = ({
  children,
}) => {
  const [headlines, setHeadlines] = useState<Headline[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedAll, setHasFetchedAll] = useState(false);

  const { user } = useAuth();

  // ✅ Transform backend => frontend
  const transformHeadlineData = (data: any): Headline => ({
    id: data._id,
    dailyMessage: data.dailyMessage || "",
    dailyMessage_in_thai: data.dailyMessage_in_thai || "",
    dailyMessage_in_arabic: data.dailyMessage_in_arabic || "",
    dailyQuestion_in_thai: data.dailyQuestion_in_thai || "",
    dailyQuestion_in_arabic: data.dailyQuestion_in_arabic || "",
    dailyQuestion: data.dailyQuestion || "",
    date: new Date(data.date),
    lucky_color: data.lucky_color || "",
    energy_level: data.energy_level || "",
    golden_hour: data.golden_hour || "",
    color_code: data.color_code || "",
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  });

  // ✅ Date key helper (YYYY-MM-DD) so store comparisons are stable
  const toDateKey = (input: string | Date) => {
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return null;
    // Make key in UTC day format (matches backend day-range logic)
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // ✅ Local getter (cache)
  const getHeadlineFromStoreByDate = useCallback(
    (date: string | Date) => {
      const key = toDateKey(date);
      if (!key) return undefined;

      return headlines.find((h) => toDateKey(h.date) === key);
    },
    [headlines],
  );

  // ✅ Fetch all (store in state)
  const fetchAllHeadlines = useCallback(async () => {
    if (!user) return;

    try {
      setIsLoading(true);

      const response = await api.get("/backend/headlines");

      if (response.data.success) {
        const transformed = (response.data.data || []).map(
          transformHeadlineData,
        );
        setHeadlines(transformed);
        setHasFetchedAll(true);
      } else {
        toast.error("โหลดหัวข้อข่าวไม่สำเร็จ");
      }
    } catch (error) {
      console.error("Failed to fetch all headlines:", error);
      toast.error("โหลดหัวข้อข่าวไม่สำเร็จ");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // ✅ Same signature style as your SubCategoryContext (returns {data, meta})
  const getAllHeadlines = useCallback(async () => {
    try {
      const response = await api.get("/backend/headlines");

      if (response.data.success) {
        const transformed = (response.data.data || []).map(
          transformHeadlineData,
        );
        setHeadlines(transformed);

        return {
          data: transformed,
          meta: { total: response.data.total ?? transformed.length },
        };
      }
      return { data: [], meta: { total: 0 } };
    } catch (error) {
      console.error("Failed to fetch all headlines:", error);
      toast.error("โหลดหัวข้อข่าวไม่สำเร็จ");
      throw error;
    }
  }, []);

  // ✅ Get by date (cache first, then API)
  const getHeadlineByDate = useCallback(
    async (date: string | Date): Promise<Headline | null> => {
      const key = toDateKey(date);
      if (!key) {
        toast.error("วันที่ไม่ถูกต้อง");
        return null;
      }

      // 1) cache first
      const cached = getHeadlineFromStoreByDate(key);
      if (cached) return cached;

      // 2) fetch from backend
      try {
        setIsLoading(true);

        // backend route: /headline/:date
        const response = await api.get(`/backend/headline/${key}`);

        if (response.data.success && response.data.data) {
          const transformed = transformHeadlineData(response.data.data);

          // merge into store (avoid duplicates by dateKey)
          setHeadlines((prev) => {
            const withoutSameDay = prev.filter(
              (h) => toDateKey(h.date) !== key,
            );
            return [transformed, ...withoutSameDay];
          });

          return transformed;
        }

        return null;
      } catch (error: any) {
        const msg = error?.response?.data?.message || "โหลดหัวข้อข่าวไม่สำเร็จ";
        toast.error(msg);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [getHeadlineFromStoreByDate],
  );

  // ✅ Auto load on login like SubCategoryContext
  useEffect(() => {
    if (!user) {
      setHeadlines([]);
      setHasFetchedAll(false);
      return;
    }

    if (user && !hasFetchedAll && !isLoading) {
      fetchAllHeadlines();
    }
  }, [user, hasFetchedAll, isLoading, fetchAllHeadlines]);

  return (
    <HeadlineContext.Provider
      value={{
        headlines,
        isLoading,
        fetchAllHeadlines,
        getAllHeadlines,
        getHeadlineByDate,
        getHeadlineFromStoreByDate,
      }}
    >
      {children}
    </HeadlineContext.Provider>
  );
};
